import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import subscriptionService from '../services/subscriptionService';
import toast from 'react-hot-toast';
import { loadStripe } from '@stripe/stripe-js';
import QRCode from 'qrcode';
import {
  Elements,
  CardElement,
  useStripe,
  useElements
} from '@stripe/react-stripe-js';
import {
  CheckIcon,
  XMarkIcon,
  CurrencyDollarIcon,
  BoltIcon,
  ShieldCheckIcon,
  StarIcon,
  SparklesIcon,
  CreditCardIcon,
  LockClosedIcon
} from '@heroicons/react/24/outline';

// PayPal SDK Types
declare global {
  interface Window {
    paypal?: any;
  }
}

interface SubscriptionPlan {
  id: string;
  name: string;
  description: string;
  userType: 'student' | 'employer';
  tier: 'free' | 'basic' | 'plus' | 'premium' | 'pro' | 'enterprise';
  monthlyPrice: number;
  yearlyPrice: number;
  currency: string;
  features: any;
  limits: any;
  isActive: boolean;
  isPopular?: boolean;
}

// Initialize Stripe
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '');

// PayPal SDK loading
const loadPayPalSDK = (): Promise<any> => {
  return new Promise((resolve, reject) => {
    if (window.paypal) {
      resolve(window.paypal);
      return;
    }
    
    const script = document.createElement('script');
    
    // Use production client ID - we have real PayPal credentials now
    const clientId = process.env.REACT_APP_PAYPAL_CLIENT_ID;
    
    if (!clientId) {
      reject(new Error('PayPal Client ID not configured'));
      return;
    }
    
    // Only log in development
    if (process.env.NODE_ENV === 'development') {
      console.log('Loading PayPal SDK with production client ID');
    }
    
    script.src = `https://www.paypal.com/sdk/js?client-id=${clientId}&currency=USD&intent=capture`;
    script.onload = () => resolve(window.paypal);
    script.onerror = reject;
    document.head.appendChild(script);
  });
};

// PayPal Buttons Component
const PayPalButtons: React.FC<{
  plan: SubscriptionPlan;
  billingCycle: 'monthly' | 'yearly';
  onSuccess: () => void;
  onError: (error: any) => void;
}> = ({ plan, billingCycle, onSuccess, onError }) => {
  const paypalContainerRef = React.useRef<HTMLDivElement>(null);
  const { currentUser } = useAuth();

  useEffect(() => {
    if (!window.paypal || !paypalContainerRef.current) return;

    const amount = billingCycle === 'monthly' ? plan.monthlyPrice : plan.yearlyPrice;

    window.paypal.Buttons({
      createOrder: async (data: any, actions: any) => {
        try {
          // Create order through our backend API instead of directly with PayPal
          const token = await currentUser?.getIdToken();
          const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/subscriptions/subscribe/intent`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({
              planId: plan.id,
              billingCycle,
              paymentMethod: 'paypal'
            })
          });

          if (!response.ok) {
            throw new Error('Failed to create PayPal order via backend');
          }

          const result = await response.json();
          return result.data.paymentIntent.id; // Return the PayPal order ID
        } catch (error) {
          console.error('Error creating PayPal order:', error);
          throw error;
        }
      },
      onApprove: async (data: any, actions: any) => {
        try {
          const order = await actions.order.capture();
          
          // Only log payment details in development
          if (process.env.NODE_ENV === 'development') {
            console.log('PayPal payment details:', { id: order.id, status: order.status });
          }
          
          // Check if payment actually succeeded
          const paymentStatus = order.purchase_units?.[0]?.payments?.captures?.[0]?.status;
          
          if (order.status !== 'COMPLETED' || paymentStatus !== 'COMPLETED') {
            throw new Error(`Payment failed. Order status: ${order.status}, Payment status: ${paymentStatus}`);
          }
          
          // Only proceed if payment is confirmed
          const response = await subscriptionService.confirmSubscription(
            order.id,
            'paypal',
            plan.id,
            billingCycle,
            { paypalOrderId: order.id, paypalOrderDetails: order }
          );
          
          if (response.success) {
            toast.success('PayPal payment successful! Subscription activated.');
            onSuccess();
          } else {
            throw new Error(response.message || 'Failed to activate subscription');
          }
        } catch (error) {
          if (process.env.NODE_ENV === 'development') {
            console.error('PayPal payment confirmation error:', error);
          }
          onError(error);
        }
      },
      onError: (err: any) => {
        console.error('PayPal Buttons error:', err);
        onError(err);
      },
      onCancel: (data: any) => {
        console.log('PayPal payment cancelled:', data);
        toast('PayPal payment was cancelled', { icon: 'ℹ️' });
      },
      style: {
        layout: 'vertical',
        color: 'blue',
        shape: 'rect',
        label: 'paypal'
      }
    }).render(paypalContainerRef.current);
  }, [plan, billingCycle, onSuccess, onError, currentUser]);

  return <div ref={paypalContainerRef} className="paypal-buttons-container"></div>;
};

// Crypto Payment Component
const CryptoPayment: React.FC<{
  plan: SubscriptionPlan;
  billingCycle: 'monthly' | 'yearly';
  selectedCrypto: string;
  onCryptoChange: (crypto: string) => void;
  onSuccess: () => void;
  onError: (error: any) => void;
}> = ({ plan, billingCycle, selectedCrypto, onCryptoChange, onSuccess, onError }) => {
  const [qrCodeUrl, setQrCodeUrl] = useState<string>('');
  const [paymentAddress, setPaymentAddress] = useState<string>('');
  const [paymentAmount, setPaymentAmount] = useState<string>('');
  const [paymentStatus, setPaymentStatus] = useState<'pending' | 'confirming' | 'confirmed' | 'failed'>('pending');
  const [timeRemaining, setTimeRemaining] = useState<number>(900); // 15 minutes
  const [paymentId, setPaymentId] = useState<string>('');

  const cryptoOptions = [
    { value: 'btc', label: 'Bitcoin (BTC)', symbol: '₿', icon: '🟠' },
    { value: 'eth', label: 'Ethereum (ETH)', symbol: 'Ξ', icon: '⚫' },
    { value: 'usdttrc20', label: 'Tether (USDT · TRC20)', symbol: '$', icon: '🟢' },
    { value: 'usdc', label: 'USD Coin (USDC)', symbol: '$', icon: '🔵' },
    { value: 'doge', label: 'Dogecoin (DOGE)', symbol: 'Ð', icon: '🟡' },
    { value: 'sol', label: 'Solana (SOL)', symbol: '◎', icon: '🟣' },
    { value: 'ada', label: 'Cardano (ADA)', symbol: '₳', icon: '🔵' },
    { value: 'dot', label: 'Polkadot (DOT)', symbol: '●', icon: '🟣' }
  ];

  // Create real NowPayments invoice
  const createNowPaymentsInvoice = async (crypto: string) => {
    try {
      const response = await subscriptionService.createPaymentIntent(
        plan.id,
        billingCycle,
        'crypto',
        crypto
      );
      
      if (response.success && response.data.paymentIntent) {
        return response.data.paymentIntent;
      } else {
        throw new Error(response.message || 'Failed to create NowPayments invoice');
      }
    } catch (error) {
      console.error('NowPayments invoice creation error:', error);
      throw error;
    }
  };

  const generatePaymentData = async () => {
    try {
      // Create real NowPayments invoice
      const nowPaymentsInvoice = await createNowPaymentsInvoice(selectedCrypto);
      
      setPaymentAddress(nowPaymentsInvoice.address);
      setPaymentAmount(nowPaymentsInvoice.amount.toString());
      setPaymentId(nowPaymentsInvoice.id);
      
      // Set expiration time from NowPayments (24 hours default)
      if (nowPaymentsInvoice.expiresAt) {
        const expirationTime = new Date(nowPaymentsInvoice.expiresAt).getTime();
        const currentTime = Date.now();
        const timeLeft = Math.max(0, Math.floor((expirationTime - currentTime) / 1000));
        setTimeRemaining(timeLeft);
      }
      
      // Generate QR code for the real payment
      const selectedCryptoData = cryptoOptions.find(c => c.value === selectedCrypto);
      const qrData = `${selectedCrypto}:${nowPaymentsInvoice.address}?amount=${nowPaymentsInvoice.amount}&label=${encodeURIComponent(`${plan.name} Subscription`)}`;
      
      const qrCodeDataUrl = await QRCode.toDataURL(qrData, {
        width: 256,
        margin: 2,
        color: {
          dark: '#000000',
          light: '#FFFFFF'
        }
      });
      
      setQrCodeUrl(qrCodeDataUrl);
    } catch (error) {
      console.error('Error generating real payment data:', error);
      onError(error);
    }
  };

  // Countdown timer
  useEffect(() => {
    if (timeRemaining > 0) {
      const timer = setTimeout(() => setTimeRemaining(timeRemaining - 1), 1000);
      return () => clearTimeout(timer);
    } else {
      setPaymentStatus('failed');
      onError(new Error('Payment timeout'));
    }
  }, [timeRemaining, onError]);

  // Generate payment data when crypto changes
  useEffect(() => {
    if (selectedCrypto) {
      generatePaymentData();
    }
  }, [selectedCrypto, plan, billingCycle]);

  // Start polling for payment status when payment is created
  useEffect(() => {
    if (paymentId && paymentStatus === 'pending') {
      const interval = setInterval(pollPaymentStatus, 10000); // Poll every 10 seconds
      
      // Also poll immediately after 30 seconds (enough time for user to send)
      const initialDelay = setTimeout(pollPaymentStatus, 30000);
      
      return () => {
        clearInterval(interval);
        clearTimeout(initialDelay);
      };
    }
  }, [paymentId, paymentStatus]);

  // Real-time payment status polling
  const pollPaymentStatus = async () => {
    if (!paymentId) return;
    
    try {
      const response = await subscriptionService.checkPaymentStatus(paymentId);
      
      if (response.success && response.data) {
        const status = response.data.status;
        const actuallyPaid = response.data.actuallyPaid;
        
        console.log('Payment status update:', { status, actuallyPaid });
        
        if (status === 'confirmed' || status === 'finished') {
          setPaymentStatus('confirmed');
          toast.success('Crypto payment confirmed! Subscription activated.');
          onSuccess();
        } else if (status === 'failed' || status === 'expired') {
          setPaymentStatus('failed');
          toast.error('Payment failed or expired. Please try again.');
        } else if (status === 'partially_paid') {
          setPaymentStatus('confirming');
          toast('Payment partially received, waiting for full amount...', { icon: '⏳' });
        } else if (status === 'sending') {
          setPaymentStatus('confirming');
          toast('Payment detected, confirming on blockchain...', { icon: '🔄' });
        }
        // For 'waiting' status, keep showing the payment interface
      }
    } catch (error) {
      console.error('Error checking payment status:', error);
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const selectedCryptoData = cryptoOptions.find(c => c.value === selectedCrypto);

  return (
    <div className="space-y-6">
      {/* Crypto Selection */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-3">
          Select Cryptocurrency
        </label>
        <div className="grid grid-cols-2 gap-3">
          {cryptoOptions.map((crypto) => (
            <button
              key={crypto.value}
              onClick={() => onCryptoChange(crypto.value)}
              className={`p-3 rounded-lg border-2 transition-all text-left ${
                selectedCrypto === crypto.value
                  ? 'border-yellow-500 bg-yellow-50 ring-2 ring-yellow-200'
                  : 'border-gray-200 hover:border-gray-300 bg-white'
              }`}
            >
              <div className="flex items-center">
                <span className="text-lg mr-2">{crypto.icon}</span>
                <div>
                  <div className="font-medium text-sm">{crypto.label}</div>
                  <div className="text-xs text-gray-500">{crypto.symbol}</div>
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Payment Details */}
      {paymentAddress && selectedCryptoData && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
          <div className="text-center mb-4">
            <div className="flex items-center justify-center mb-2">
              <span className="text-2xl mr-2">{selectedCryptoData.icon}</span>
              <h3 className="text-lg font-semibold text-gray-900">
                Pay with {selectedCryptoData.label}
              </h3>
            </div>
            
            {paymentStatus === 'pending' && (
              <div className="text-sm text-orange-600 font-medium">
                Payment expires in: {formatTime(timeRemaining)}
              </div>
            )}
          </div>

          {/* QR Code */}
          <div className="flex justify-center mb-4">
            {qrCodeUrl && (
              <div className="bg-white p-4 rounded-lg shadow-sm border">
                <img src={qrCodeUrl} alt="Payment QR Code" className="w-48 h-48" />
                <p className="text-xs text-gray-500 text-center mt-2">
                  Scan with your {selectedCryptoData.label} wallet
                </p>
              </div>
            )}
          </div>

          {/* Payment Amount */}
          <div className="bg-white rounded-lg p-4 mb-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-gray-900 mb-1">
                {paymentAmount} {selectedCryptoData.symbol}
              </div>
              <div className="text-sm text-gray-600">
                ≈ ${billingCycle === 'monthly' ? plan.monthlyPrice : plan.yearlyPrice} USD
              </div>
            </div>
          </div>

          {/* Payment Address */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Send to Address:
            </label>
            <div className="flex items-center bg-white border border-gray-300 rounded-md p-3">
              <input
                type="text"
                value={paymentAddress}
                readOnly
                className="flex-1 text-sm font-mono bg-transparent border-none outline-none"
              />
              <button
                onClick={() => {
                  navigator.clipboard.writeText(paymentAddress);
                  toast.success('Address copied!');
                }}
                className="ml-2 px-3 py-1 bg-gray-100 hover:bg-gray-200 rounded text-xs font-medium transition-colors"
              >
                Copy
              </button>
            </div>
          </div>

          {/* Instructions */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
            <h4 className="font-medium text-blue-900 mb-2">Payment Instructions:</h4>
            <ol className="text-sm text-blue-800 space-y-1 list-decimal list-inside">
              <li>Send exactly <strong>{paymentAmount} {selectedCryptoData.symbol}</strong> to the address above</li>
              <li>Or scan the QR code with your wallet app</li>
              <li>We'll automatically detect your payment on the blockchain</li>
              <li>Wait for blockchain confirmation (1-6 confirmations)</li>
            </ol>
            <div className="mt-3 p-2 bg-blue-100 rounded text-xs text-blue-700">
              <strong>⚡ Automatic Detection:</strong> No need to click anything - we monitor the blockchain in real-time!
            </div>
          </div>

          {/* Payment Status */}
          <div className="space-y-3">
            {paymentStatus === 'pending' && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center justify-center">
                  <div className="animate-pulse h-3 w-3 bg-blue-500 rounded-full mr-3"></div>
                  <span className="text-blue-800 font-medium">
                    Waiting for payment... Checking blockchain automatically
                  </span>
                </div>
                <p className="text-xs text-blue-600 text-center mt-2">
                  We'll automatically detect your payment once it's sent to the blockchain
                </p>
              </div>
            )}
            
            {paymentStatus === 'confirming' && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-yellow-600 mr-3"></div>
                  <span className="text-yellow-800 font-medium">
                    Payment detected! Confirming on blockchain...
                  </span>
                </div>
                <p className="text-xs text-yellow-600 text-center mt-2">
                  This usually takes 1-6 confirmations depending on the network
                </p>
              </div>
            )}
            
            {paymentStatus === 'confirmed' && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-center justify-center">
                  <span className="text-green-600 text-2xl mr-2">✓</span>
                  <span className="text-green-800 font-medium">
                    Payment Confirmed! Subscription Activated
                  </span>
                </div>
              </div>
            )}
            
            {paymentStatus === 'failed' && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-center justify-center">
                  <span className="text-red-600 text-xl mr-2">✗</span>
                  <span className="text-red-800 font-medium">
                    Payment Failed or Expired
                  </span>
                </div>
                <p className="text-xs text-red-600 text-center mt-2">
                  Please try again or contact support if you sent the payment
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Payment Modal Component
const PaymentModal: React.FC<{
  plan: SubscriptionPlan;
  billingCycle: 'monthly' | 'yearly';
  paymentMethod: 'stripe' | 'paypal' | 'crypto' | 'free';
  onClose: () => void;
  onSuccess: () => void;
}> = ({ plan, billingCycle, paymentMethod, onClose, onSuccess }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [paymentIntent, setPaymentIntent] = useState<string | null>(null);
  const [selectedCrypto, setSelectedCrypto] = useState('btc');
  const [paypalLoaded, setPaypalLoaded] = useState(false);

  // Load PayPal SDK when needed
  useEffect(() => {
    if (paymentMethod === 'paypal' && !paypalLoaded) {
      loadPayPalSDK()
        .then(() => setPaypalLoaded(true))
        .catch(error => {
          console.error('Failed to load PayPal SDK:', error);
          toast.error('Failed to load PayPal payment system');
        });
    }
  }, [paymentMethod, paypalLoaded]);

  const handlePayment = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!stripe || !elements) {
      toast.error('Stripe is not loaded');
      return;
    }

    setProcessing(true);

    try {
      // Create payment intent
      const intentResponse = await subscriptionService.createPaymentIntent(
        plan.id,
        billingCycle,
        paymentMethod,
        paymentMethod === 'crypto' ? selectedCrypto : undefined
      );

      if (!intentResponse.success || !intentResponse.data) {
        throw new Error(intentResponse.message || 'Failed to create payment intent');
      }

      const { clientSecret, paymentIntentId } = intentResponse.data;
      setPaymentIntent(paymentIntentId);

      if (paymentMethod === 'stripe') {
        if (!clientSecret) {
          throw new Error('Missing client secret for Stripe payment');
        }
        const cardElement = elements.getElement(CardElement);
        if (!cardElement) {
          throw new Error('Card element not found');
        }

        const { error, paymentIntent: confirmedPayment } = await stripe.confirmCardPayment(clientSecret, {
          payment_method: {
            card: cardElement,
          }
        });

        if (error) {
          throw new Error(error.message || 'Payment failed');
        }

        if (confirmedPayment.status === 'succeeded') {
          // Confirm subscription on backend
          const confirmResponse = await subscriptionService.confirmSubscription(
            paymentIntentId,
            paymentMethod,
            plan.id,
            billingCycle
          );

          if (confirmResponse.success) {
            toast.success('Subscription activated successfully!');
            onSuccess();
          } else {
            throw new Error(confirmResponse.message || 'Failed to activate subscription');
          }
        }
      } else if (paymentMethod === 'paypal') {
        // Handle PayPal payment
        const { approvalUrl } = intentResponse.data.paymentIntent;
        if (approvalUrl) {
          // Open PayPal approval URL in new window
          const paypalWindow = window.open(approvalUrl, 'paypal', 'width=600,height=700');
          
          // Listen for PayPal completion (you'd need to implement a callback page)
          toast('Complete your PayPal payment in the new window', { 
            icon: 'ℹ️',
            duration: 4000 
          });
          onClose(); // Close modal while user completes PayPal flow
        }
      } else if (paymentMethod === 'crypto') {
        // Handle crypto payment
        const { paymentUrl, address, amount, cryptoCurrency } = intentResponse.data.paymentIntent;
        
        // For now, show payment details
        toast.success(`Send ${amount} ${cryptoCurrency?.toUpperCase()} to: ${address}`);
        
        // You could also open the payment URL
        if (paymentUrl) {
          window.open(paymentUrl, '_blank');
        }
        
        // For demo purposes, simulate successful payment after 5 seconds
        setTimeout(async () => {
          try {
            const confirmResponse = await subscriptionService.confirmSubscription(
              paymentIntentId,
              paymentMethod,
              plan.id,
              billingCycle
            );
            
            if (confirmResponse.success) {
              toast.success('Crypto payment confirmed! Subscription activated.');
              onSuccess();
            }
          } catch (error) {
            console.error('Crypto payment confirmation error:', error);
          }
        }, 5000);
        
        onClose(); // Close modal
      }
    } catch (error: any) {
      console.error('Payment error:', error);
      toast.error(error.message || 'Payment failed');
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
      <div className="relative bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 flex-shrink-0">
          <h3 className="text-lg font-medium text-gray-900">
            Subscribe to {plan.name}
          </h3>
          <button
            onClick={onClose}
            disabled={processing}
            className="text-gray-400 hover:text-gray-600 focus:outline-none focus:text-gray-600"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto flex-1">
          <div className="mb-6">
            <div className="text-center">
              <div className="text-3xl font-bold text-gray-900">
                {subscriptionService.formatCurrency(
                  billingCycle === 'monthly' ? plan.monthlyPrice : plan.yearlyPrice,
                  plan.currency
                )}
              </div>
              <div className="text-sm text-gray-500">per {billingCycle === 'monthly' ? 'month' : 'year'}</div>
              {billingCycle === 'yearly' && plan.monthlyPrice > 0 && (
                <div className="text-sm text-green-600 mt-1">
                  Save {subscriptionService.calculateYearlySavingsPercentage(plan.monthlyPrice, plan.yearlyPrice).toFixed(0)}% yearly
                </div>
              )}
            </div>
          </div>

          {paymentMethod === 'stripe' && (
            <form onSubmit={handlePayment} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <CreditCardIcon className="inline h-4 w-4 mr-1" />
                  Card Information
                </label>
                <div className="border border-gray-300 rounded-md p-3">
                  <CardElement
                    options={{
                      style: {
                        base: {
                          fontSize: '16px',
                          color: '#424770',
                          '::placeholder': {
                            color: '#aab7c4',
                          },
                        },
                      },
                    }}
                  />
                </div>
              </div>

              <div className="flex items-center text-xs text-gray-500">
                <LockClosedIcon className="h-3 w-3 mr-1" />
                Your payment information is secure and encrypted
              </div>

              <button
                type="submit"
                disabled={processing || !stripe}
                className="w-full bg-indigo-600 text-white py-3 px-4 rounded-md hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center font-medium"
              >
                {processing ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Processing...
                  </>
                ) : (
                  `Subscribe for ${subscriptionService.formatCurrency(
                    billingCycle === 'monthly' ? plan.monthlyPrice : plan.yearlyPrice,
                    plan.currency
                  )}`
                )}
              </button>
            </form>
          )}

          {paymentMethod === 'paypal' && (
            <div className="space-y-4">
              <div className="text-center">
                <div className="bg-blue-50 p-4 rounded-lg mb-4">
                  <BoltIcon className="h-8 w-8 text-blue-500 mx-auto mb-2" />
                  <h4 className="font-medium text-blue-900">Card Payment</h4>
                  <p className="text-sm text-blue-700 mt-1">
                    Pay securely by debit/credit card or PayPal
                  </p>
                </div>
              </div>

              <div className="flex items-center text-xs text-gray-500 justify-center mb-4">
                <LockClosedIcon className="h-3 w-3 mr-1" />
                Secured by PayPal's industry-leading protection
              </div>

              {paypalLoaded ? (
                <PayPalButtons 
                  plan={plan} 
                  billingCycle={billingCycle} 
                  onSuccess={onSuccess} 
                  onError={(error) => {
                    console.error('PayPal payment error:', error);
                    toast.error('PayPal payment failed: ' + error.message);
                  }}
                />
              ) : (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mr-3"></div>
                  <span className="text-gray-600">Loading PayPal...</span>
                </div>
              )}
            </div>
          )}

          {paymentMethod === 'crypto' && (
            <CryptoPayment
              plan={plan}
              billingCycle={billingCycle}
              selectedCrypto={selectedCrypto}
              onCryptoChange={setSelectedCrypto}
              onSuccess={onSuccess}
              onError={(error) => {
                console.error('Crypto payment error:', error);
                toast.error('Crypto payment failed: ' + error.message);
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
};

const SubscriptionPageContent: React.FC = () => {
  const { currentUser, userProfile } = useAuth();
  const navigate = useNavigate();
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [currentSubscription, setCurrentSubscription] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'yearly'>('monthly');
  // Stripe card processing is temporarily disabled while it's being set up.
  // PayPal is the sole card processor for now — default to it.
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<'stripe' | 'paypal' | 'crypto' | 'free'>('paypal');
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<SubscriptionPlan | null>(null);

  const userType = userProfile?.userType || 'student';

  useEffect(() => {
    if (!currentUser) {
      navigate('/login');
      return;
    }
    fetchPlans();
    fetchCurrentSubscription();
  }, [currentUser, navigate]);

  const fetchPlans = async () => {
    try {
      const response = await subscriptionService.getPlans(userType);
      if (response.success && response.data) {
        // Mark popular plans
        const plansWithPopular = response.data.plans.map((plan: SubscriptionPlan) => ({
          ...plan,
          isPopular: (userType === 'student' && plan.tier === 'plus') || 
                    (userType === 'employer' && plan.tier === 'premium')
        }));
        setPlans(plansWithPopular);
      }
    } catch (error: any) {
      console.error('Failed to fetch plans:', error);
      toast.error('Failed to load subscription plans');
    }
  };

  const fetchCurrentSubscription = async () => {
    try {
      const response = await subscriptionService.getCurrentSubscription();
      if (response.success && response.data) {
        setCurrentSubscription(response.data);
      }
    } catch (error: any) {
      console.error('Failed to fetch current subscription:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = async (planId: string) => {
    if (!currentUser) {
      navigate('/login');
      return;
    }

    const plan = plans.find(p => p.id === planId);
    if (!plan) {
      toast.error('Plan not found');
      return;
    }

    // Check if it's a free plan
    const price = billingCycle === 'monthly' ? plan.monthlyPrice : plan.yearlyPrice;
    if (price === 0) {
      try {
        // Directly activate free plan
        const response = await subscriptionService.confirmSubscription(
          'free_plan',
          'free',
          planId,
          billingCycle
        );

        if (response.success) {
          toast.success('Free plan activated successfully!');
          await fetchCurrentSubscription();
          navigate('/dashboard');
        } else {
          toast.error(response.message || 'Failed to activate free plan');
        }
      } catch (error: any) {
        console.error('Free plan activation error:', error);
        toast.error('Failed to activate free plan');
      }
      return;
    }

    // For paid plans, show payment modal
    setSelectedPlan(plan);
    setShowPaymentModal(true);
  };

  const handlePaymentSuccess = async () => {
    setShowPaymentModal(false);
    setSelectedPlan(null);
    await fetchCurrentSubscription();
    navigate('/manage-subscription');
  };

  const getPlanIcon = (tier: string) => {
    switch (tier) {
      case 'free': return '🆓';
      case 'basic': return '📋';
      case 'plus': return '⭐';
      case 'premium': return '💎';
      case 'pro': return '🚀';
      case 'enterprise': return '🏢';
      default: return '📋';
    }
  };

  // Define tier hierarchy for upgrade/downgrade logic
  const getTierValue = (tier: string): number => {
    switch (tier) {
      case 'free': return 0;
      case 'basic': return 1;
      case 'plus': return 2;
      case 'premium': return 3;
      case 'pro': return 4;
      case 'enterprise': return 5;
      default: return 0;
    }
  };

  const getPlanAction = (plan: SubscriptionPlan) => {
    if (!currentSubscription) {
      return { action: 'subscribe', text: 'Subscribe to', className: 'bg-blue-600 hover:bg-blue-700' };
    }

    const currentTierValue = getTierValue(currentSubscription.plan?.tier || 'free');
    const targetTierValue = getTierValue(plan.tier);

    if (currentSubscription.planId === plan.id) {
      return { action: 'current', text: 'Current Plan', className: 'bg-gray-400 cursor-not-allowed', disabled: true };
    } else if (targetTierValue > currentTierValue) {
      return { action: 'upgrade', text: 'Upgrade to', className: 'bg-green-600 hover:bg-green-700' };
    } else if (targetTierValue < currentTierValue) {
      return { action: 'downgrade', text: 'Downgrade to', className: 'bg-orange-600 hover:bg-orange-700' };
    } else {
      return { action: 'switch', text: 'Switch to', className: 'bg-blue-600 hover:bg-blue-700' };
    }
  };

  const getPlanColor = (tier: string) => {
    switch (tier) {
      case 'free': return 'border-gray-200 text-gray-900';
      case 'basic': return 'border-blue-200 text-blue-900';
      case 'plus': return 'border-indigo-200 text-indigo-900';
      case 'premium': return 'border-purple-200 text-purple-900';
      case 'pro': return 'border-pink-200 text-pink-900';
      case 'enterprise': return 'border-red-200 text-red-900';
      default: return 'border-gray-200 text-gray-900';
    }
  };

  const getButtonColor = (tier: string) => {
    switch (tier) {
      case 'free': return 'bg-gray-600 hover:bg-gray-700';
      case 'basic': return 'bg-blue-600 hover:bg-blue-700';
      case 'plus': return 'bg-indigo-600 hover:bg-indigo-700';
      case 'premium': return 'bg-purple-600 hover:bg-purple-700';
      case 'pro': return 'bg-pink-600 hover:bg-pink-700';
      case 'enterprise': return 'bg-red-600 hover:bg-red-700';
      default: return 'bg-gray-600 hover:bg-gray-700';
    }
  };

  const isCurrentPlan = (planId: string) => {
    return currentSubscription?.planId === planId || currentSubscription?.plan?.id === planId;
  };

  const getFeaturesList = (features: any, userType: string) => {
    const relevantFeatures = userType === 'student' 
      ? [
          'allCoursesAccess',
          'priorityJobMatching', 
          'advancedAnalytics',
          'aiCvGeneration',
          'detailedEmployerInsights',
          'prioritySupport',
          'customProfileThemes',
          'skillAssessmentRetakes',
          'learningPathRecommendations',
          'mentorshipAccess',
          'exclusiveWebinars',
          'portfolioBuilder'
        ]
      : [
          'jobPostingAccess',
          'candidateSearchAccess',
          'backgroundCheckAccess',
          'socialMediaAnalysisAccess',
          'customCourseCreation',
          'advancedFiltering',
          'bulkMessaging',
          'detailedCandidateAnalytics',
          'longitudinalDataAccess',
          'customBranding',
          'apiAccess',
          'dedicatedAccountManager',
          'whitelabelSolution',
          'ssoIntegration',
          'customIntegrations'
        ];

    return relevantFeatures.map(feature => ({
      name: subscriptionService.getFeatureDisplayName(feature),
      included: features?.[feature] === true
    }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center">
          <h1 className="text-4xl font-bold text-gray-900 sm:text-5xl">
            Choose Your Plan
          </h1>
          <p className="mt-4 text-xl text-gray-600">
            {userType === 'student' 
              ? 'Unlock your potential with premium learning features'
              : 'Find the best candidates with our employer tools'
            }
          </p>
          
          {/* Current Subscription Status */}
          {currentSubscription && (
            <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg inline-flex items-center">
              <ShieldCheckIcon className="h-5 w-5 text-blue-600 mr-2" />
              <span className="text-blue-800">
                Current plan: <strong>{currentSubscription.plan?.name || 'Unknown'}</strong>
              </span>
            </div>
          )}
        </div>

        {/* Billing Cycle Toggle */}
        <div className="flex justify-center mt-10">
          <div className="bg-white p-1.5 rounded-lg border border-gray-200 shadow-sm">
            <button
              onClick={() => setBillingCycle('monthly')}
              className={`px-6 py-2.5 text-sm font-medium rounded-md transition-colors ${
                billingCycle === 'monthly'
                  ? 'bg-indigo-600 text-white shadow-sm'
                  : 'text-gray-700 hover:text-gray-900 hover:bg-gray-50'
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setBillingCycle('yearly')}
              className={`ml-1 px-6 py-2.5 text-sm font-medium rounded-md transition-colors ${
                billingCycle === 'yearly'
                  ? 'bg-indigo-600 text-white shadow-sm'
                  : 'text-gray-700 hover:text-gray-900 hover:bg-gray-50'
              }`}
            >
              Yearly
              <span className="ml-2 text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded-full font-semibold">
                Save {subscriptionService.calculateYearlySavingsPercentage(99, 999).toFixed(0)}%
              </span>
            </button>
          </div>
        </div>

        {/* Payment Method Selection */}
        <div className="flex justify-center mt-8">
          <div className="bg-white p-1.5 rounded-lg border border-gray-200 shadow-sm">
            {/* Stripe card option temporarily disabled while Stripe is being set up.
                Re-enable this button (and set the default method back to 'stripe')
                once Stripe is live.
            <button
              onClick={() => setSelectedPaymentMethod('stripe')}
              className={`px-4 py-2.5 text-sm font-medium rounded-md transition-colors ${
                selectedPaymentMethod === 'stripe'
                  ? 'bg-blue-600 text-white shadow-sm'
                  : 'text-gray-700 hover:text-gray-900 hover:bg-gray-50'
              }`}
            >
              💳 Card
            </button>
            */}
            <button
              onClick={() => setSelectedPaymentMethod('paypal')}
              className={`px-4 py-2.5 text-sm font-medium rounded-md transition-colors ${
                selectedPaymentMethod === 'paypal'
                  ? 'bg-blue-600 text-white shadow-sm'
                  : 'text-gray-700 hover:text-gray-900 hover:bg-gray-50'
              }`}
            >
              💳 Card
            </button>
            <button
              onClick={() => setSelectedPaymentMethod('crypto')}
              className={`ml-1 px-4 py-2.5 text-sm font-medium rounded-md transition-colors ${
                selectedPaymentMethod === 'crypto'
                  ? 'bg-blue-600 text-white shadow-sm'
                  : 'text-gray-700 hover:text-gray-900 hover:bg-gray-50'
              }`}
            >
              ₿ Crypto
            </button>
          </div>
        </div>

        {/* Plans Grid */}
        <div className="mt-12">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-3 xl:gap-12">
            {plans.map((plan) => {
              const price = billingCycle === 'monthly' ? plan.monthlyPrice : plan.yearlyPrice;
              const features = getFeaturesList(plan.features, userType);
              const isCurrentUserPlan = isCurrentPlan(plan.id);

              return (
                <div
                  key={plan.id}
                  className={`relative rounded-2xl border-2 bg-white p-8 shadow-lg ${
                    isCurrentUserPlan
                      ? 'border-green-500 ring-2 ring-green-200 bg-green-50'
                      : plan.isPopular 
                        ? 'border-indigo-600 ring-2 ring-indigo-600 ring-opacity-20'
                        : getPlanColor(plan.tier)
                  }`}
                >
                  {/* Current Plan Badge */}
                  {isCurrentUserPlan && (
                    <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                      <div className="bg-green-600 text-white px-4 py-1 rounded-full text-sm font-medium flex items-center">
                        <CheckIcon className="h-4 w-4 mr-1" />
                        Current Plan
                      </div>
                    </div>
                  )}
                  
                  {/* Popular Badge */}
                  {plan.isPopular && !isCurrentUserPlan && (
                    <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                      <div className="bg-indigo-600 text-white px-4 py-1 rounded-full text-sm font-medium flex items-center">
                        <StarIcon className="h-4 w-4 mr-1" />
                        Most Popular
                      </div>
                    </div>
                  )}

                  {/* Plan Header */}
                  <div className="text-center">
                    <div className="text-4xl mb-2">{getPlanIcon(plan.tier)}</div>
                    <h3 className="text-2xl font-bold text-gray-900">{plan.name}</h3>
                    <p className="mt-2 text-gray-600">{plan.description}</p>
                  </div>

                  {/* Pricing */}
                  <div className="mt-6 text-center">
                    <div className="flex items-baseline justify-center">
                      <span className="text-4xl font-bold text-gray-900">
                        {subscriptionService.formatCurrency(price)}
                      </span>
                      <span className="ml-1 text-lg text-gray-600">
                        /{billingCycle === 'monthly' ? 'month' : 'year'}
                      </span>
                    </div>
                    {billingCycle === 'yearly' && plan.monthlyPrice > 0 && (
                      <p className="mt-1 text-sm text-green-600">
                        Save {subscriptionService.formatCurrency(
                          subscriptionService.calculateYearlySavings(plan.monthlyPrice, plan.yearlyPrice)
                        )} annually
                      </p>
                    )}
                  </div>

                  {/* Features */}
                  <ul className="mt-8 space-y-3">
                    {features.slice(0, 8).map((feature, index) => (
                      <li key={index} className="flex items-start">
                        {feature.included ? (
                          <CheckIcon className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                        ) : (
                          <XMarkIcon className="h-5 w-5 text-gray-300 mr-3 mt-0.5 flex-shrink-0" />
                        )}
                        <span className={`text-sm ${
                          feature.included ? 'text-gray-900' : 'text-gray-500'
                        }`}>
                          {feature.name}
                        </span>
                      </li>
                    ))}
                    {features.length > 8 && (
                      <li className="text-sm text-gray-500 italic">
                        + {features.length - 8} more features
                      </li>
                    )}
                  </ul>

                  {/* Action Button */}
                  <div className="mt-8">
                    {isCurrentUserPlan ? (
                      <button
                        disabled
                        className="w-full py-3 px-4 border border-gray-300 rounded-lg text-gray-500 font-medium bg-gray-50 cursor-not-allowed"
                      >
                        Current Plan
                      </button>
                    ) : plan.tier === 'free' ? (
                      <button
                        onClick={() => navigate('/dashboard')}
                        className="w-full py-3 px-4 bg-gray-600 hover:bg-gray-700 text-white font-medium rounded-lg transition-colors"
                      >
                        Get Started Free
                      </button>
                    ) : (
                      <button
                        onClick={() => !getPlanAction(plan).disabled && handleSubscribe(plan.id)}
                        disabled={getPlanAction(plan).disabled}
                        className={`w-full py-3 px-4 text-white font-medium rounded-lg transition-colors ${
                          getPlanAction(plan).disabled ? getPlanAction(plan).className : getButtonColor(plan.tier)
                        }`}
                      >
                        {getPlanAction(plan).text} {plan.name}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* FAQ Section */}
        <div className="mt-20">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">
            Frequently Asked Questions
          </h2>
          <div className="max-w-3xl mx-auto">
            <div className="space-y-8">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  Can I change my plan anytime?
                </h3>
                <p className="mt-2 text-gray-600">
                  Yes, you can upgrade or downgrade your plan at any time. Changes will be reflected in your next billing cycle.
                </p>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  What payment methods do you accept?
                </h3>
                <p className="mt-2 text-gray-600">
                  We accept all major credit cards via Stripe, PayPal, and cryptocurrency payments including Bitcoin and Ethereum.
                </p>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  Is there a free trial?
                </h3>
                <p className="mt-2 text-gray-600">
                  {userType === 'student' 
                    ? 'All courses are free for students. Premium features come with paid plans.'
                    : 'We offer a 14-day free trial for all employer plans so you can test our platform.'
                  }
                </p>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  Can I cancel anytime?
                </h3>
                <p className="mt-2 text-gray-600">
                  Yes, you can cancel your subscription at any time. You'll continue to have access until the end of your current billing period.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Payment Modal */}
      {showPaymentModal && selectedPlan && (
        <PaymentModal
          plan={selectedPlan}
          billingCycle={billingCycle}
          paymentMethod={selectedPaymentMethod}
          onClose={() => {
            setShowPaymentModal(false);
            setSelectedPlan(null);
          }}
          onSuccess={handlePaymentSuccess}
        />
      )}
    </div>
  );
};

// Main component wrapped with Stripe Elements
const SubscriptionPage: React.FC = () => {
  return (
    <Elements stripe={stripePromise}>
      <SubscriptionPageContent />
    </Elements>
  );
};

export default SubscriptionPage;